import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, useDeviceType } from '@cian/ui-kit';

import { PaginationNew } from '../../components/Pagination';
import { IPaginationItem } from '../../types/pagination';
import { correctPathname, preparePageNumber, useCurrentListingTotalPages, useCurrentListingStatus } from '../../utils';
import { generatePagesData } from './utils';
import { setInitialPageNumber, setListingAppendType } from '../../actions/settings';
import { ERequestStatus } from '../../types/requestStatus';
import { ISettings } from '../../types/settings';
import * as s from './PaginationContainer.css';

/**
 * Строит список пагинации для листинга
 * (1 ... 3 4 5 6 7 [8] 9 10 11 12 13 ...)
 */
export const PaginationContainer = () => {
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [activeNumber, setActiveNumber] = useState(preparePageNumber(searchParams.get('page') as string));
  const history = useHistory();
  const totalPages = useCurrentListingTotalPages();
  const listingStatus = useCurrentListingStatus();

  /**
   * Слушает изменение page в qs
   * и обновляет номер активной страницы
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const qsPage = preparePageNumber(searchParams.get('page') as string);

    if (activeNumber === qsPage) {
      return;
    }

    setActiveNumber(qsPage);
  }, [activeNumber, search]);

  /**
   * Устанавливает номер активной страницы, как page в qs,
   * (сохраняет остальные ключи qs)
   */
  const handleClick = useCallback(
    (number: IPaginationItem['number'], type: ISettings['listingAppendType']) => {
      const searchParams = new URLSearchParams(search);

      if (number === 1) {
        searchParams.delete('page');
      } else {
        searchParams.set('page', String(number));
      }

      /**
       * Переписывает индекс страницы инициализации
       * только для режима set
       */
      if (type === 'set') {
        dispatch(setInitialPageNumber(number));
      }

      /**
       * Запоминает в сторе, как обновить список - догрузить/заменить
       */
      dispatch(setListingAppendType(type));

      history.push(`${correctPathname(pathname)}?${searchParams.toString()}`);
    },
    [dispatch, history, pathname, search],
  );

  if (totalPages <= 1 || totalPages < activeNumber) {
    return null;
  }

  return (
    <div className={s['wrapper']} data-web-ui-test-id="PaginationContainer">
      {activeNumber !== totalPages && (
        <Button
          theme={'fill_secondary'}
          fullWidth
          loading={listingStatus === ERequestStatus.Loading}
          onClick={() => handleClick(activeNumber + 1, 'append')}
        >
          Показать больше
        </Button>
      )}

      <PaginationNew
        pages={generatePagesData({
          maxShownPages: isPhone() ? 3 : 5,
          showFirstPage: isPhone(),
          activeNumber,
          pathname,
          searchParams,
          totalPages,
        })}
        activeNumber={activeNumber}
        onClick={number => handleClick(number, 'set')}
      />
    </div>
  );
};
